export type InputCaretCoordinates = {
  top: number
  left: number
  height: number
}

const MIRROR_STYLE_PROPERTIES = [
  'direction',
  'boxSizing',
  'width',
  'height',
  'overflowX',
  'overflowY',
  'borderTopWidth',
  'borderRightWidth',
  'borderBottomWidth',
  'borderLeftWidth',
  'borderStyle',
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'fontStyle',
  'fontVariant',
  'fontWeight',
  'fontStretch',
  'fontSize',
  'fontSizeAdjust',
  'lineHeight',
  'fontFamily',
  'textAlign',
  'textTransform',
  'textIndent',
  'textDecoration',
  'letterSpacing',
  'wordSpacing',
  'tabSize',
] as const

/**
 * Returns caret coordinates relative to the input's padding box, accounting for
 * scroll position. Suitable for absolutely positioning UI under the caret.
 */
export function getInputCaretCoordinates(
  element: HTMLInputElement | HTMLTextAreaElement,
  position: number,
): InputCaretCoordinates {
  const style = window.getComputedStyle(element)
  const isTextarea = element.nodeName === 'TEXTAREA'

  const mirror = document.createElement('div')
  mirror.style.position = 'absolute'
  mirror.style.visibility = 'hidden'
  mirror.style.overflow = 'hidden'
  mirror.style.whiteSpace = isTextarea ? 'pre-wrap' : 'pre'
  mirror.style.wordWrap = 'break-word'

  for (const property of MIRROR_STYLE_PROPERTIES) {
    mirror.style.setProperty(property, style.getPropertyValue(property))
  }

  mirror.style.width = `${element.clientWidth}px`

  const value = element.value
  const textBefore = value.slice(0, position)
  const textAfter = value.slice(position)

  mirror.textContent = textBefore

  const marker = document.createElement('span')
  marker.textContent = textAfter[0] ?? '.'
  mirror.appendChild(marker)

  document.body.appendChild(mirror)

  const lineHeight =
    Number.parseFloat(style.lineHeight) || marker.offsetHeight || 20

  const coordinates: InputCaretCoordinates = {
    top: marker.offsetTop - element.scrollTop,
    left: marker.offsetLeft - element.scrollLeft,
    height: lineHeight,
  }

  document.body.removeChild(mirror)

  return coordinates
}
